import { z } from "zod";
import type { PlatformRole } from "./permissions";
import type { BadgeSummary } from "./rich";
import type { CommunitySummary, UserSummary } from "./types";

/*
 * Site-wide moderation by Atrium staff (platform role moderator or admin).
 *
 * Staff are full administrators in every community, can join any community
 * (private, banned or full), and can't be moderated by community members.
 * They enforce against communities (warn, limit, suspend) and accounts
 * (warn, restrict, suspend). Every staff action lands in the platform audit log.
 */

export const COMMUNITY_PLATFORM_STATUSES = ["active", "limited", "suspended"] as const;
export type CommunityPlatformStatus = (typeof COMMUNITY_PLATFORM_STATUSES)[number];

export type StrikeTarget = "community" | "user";
/** warning: a notice the managers / account must acknowledge. limit: hidden from discovery. restrict: account is read-only. suspend: frozen. */
export type StrikeKind = "warning" | "limit" | "suspend" | "restrict";

export const STRIKE_LABEL: Record<StrikeKind, string> = {
  warning: "Warning",
  limit: "Limited",
  restrict: "Restricted",
  suspend: "Suspended",
};

export interface PlatformStrike {
  id: string;
  targetType: StrikeTarget;
  kind: StrikeKind;
  reason: string;
  /** Internal note: staff only. */
  note?: string;
  /** Staff only: who issued it. Communities and accounts see "Atrium staff". */
  actor?: UserSummary | null;
  createdAt: string;
  expiresAt: string | null;
  acknowledgedAt: string | null;
  revokedAt: string | null;
  revokedBy?: UserSummary | null;
}

/** Platform state of a community, as its members see it. */
export interface CommunityPlatformState {
  status: CommunityPlatformStatus;
  reason: string;
  until: string | null;
  /** Unacknowledged warnings: managers and staff only. */
  warnings?: PlatformStrike[];
}

export interface StaffCommunityRow {
  community: CommunitySummary;
  owner: UserSummary | null;
  status: CommunityPlatformStatus;
  statusReason: string;
  statusUntil: string | null;
  /** Warnings not revoked. */
  warningCount: number;
  createdAt: string;
}

export interface StaffCommunityDetail extends StaffCommunityRow {
  strikes: PlatformStrike[];
  channelCount: number;
  messageCount: number;
  /** Whether the viewing staff member is already in it. */
  staffIsMember: boolean;
}

export interface StaffUserRow {
  user: UserSummary;
  email: string;
  platformRole: PlatformRole;
  restrictedUntil: string | null;
  restrictionReason: string;
  suspended: boolean;
  warningCount: number;
  createdAt: string;
}

export interface StaffUserDetail extends StaffUserRow {
  strikes: PlatformStrike[];
  communitiesOwned: CommunitySummary[];
  communityCount: number;
  postCount: number;
  followerCount: number;
  /** The hand-given platform badges (STAFF_GRANTED_BADGES) and whether they hold each. */
  grantableBadges: { badge: BadgeSummary; held: boolean }[];
}

export interface PlatformAuditEntry {
  id: string;
  actor: UserSummary | null;
  action: string;
  targetType: string;
  targetId: string | null;
  targetName: string;
  details: Record<string, unknown>;
  createdAt: string;
}

export interface StaffOverview {
  communities: { total: number; limited: number; suspended: number };
  users: { total: number; restricted: number; suspended: number; staff: number };
  openWarnings: number;
}

const reason = z.string().trim().min(3, "Give a reason").max(1000);
const note = z.string().trim().max(2000).default("");
/** Duration in days; null = until lifted. Ignored for warnings. */
const days = z.number().int().min(1).max(3650).nullable().default(null);

export const staffCommunityActionSchema = z.object({ kind: z.enum(["warning", "limit", "suspend"]), reason, note, days });
export const staffUserActionSchema = z.object({ kind: z.enum(["warning", "restrict", "suspend"]), reason, note, days });
export const staffLiftSchema = z.object({ note });
export const staffRoleSchema = z.object({ role: z.enum(["user", "moderator", "admin"]) });
export const staffSearchQuery = z.object({
  q: z.string().trim().max(100).default(""),
  status: z.enum(["all", "active", "limited", "suspended", "restricted", "staff", "warned"]).default("all"),
  cursor: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export type StaffCommunityAction = z.infer<typeof staffCommunityActionSchema>;
export type StaffUserAction = z.infer<typeof staffUserActionSchema>;
